"use client" /* 1 — Hero */ /* 3 — Services */ /* 4 — Journey */ /* 5 — Industries */ /* 6 — Selected work */ /* 7 — Easier Daily Work */ /* 8 — Why ORKEST */ /* 9 — CTA */
import { HeroFlow } from "../components/hero-flow"
import { HeroBackground } from "../components/hero-background"
import { Link, useT } from "../lib/router"
import { coreServices } from "../content/services"
import { solutions, caseStudies } from "../content/catalog"
import { CEOMessage } from "../components/ceo-message"
import {
  Button,
  Container,
  CTASection,
  Eyebrow,
  Icon,
  LinkCard,
  Section,
  SectionHeading,
  type AnyIcon,
} from "../components/ui"

export const journey = [
  {
    n: "01",
    title: { en: "Establish", ar: "التأسيس" },
    body: {
      en: "Create the company\u2019s digital identity and online presence.",
      ar: "بناء الهوية الرقمية للشركة وحضورها على الإنترنت.",
    },
    to: "/services/digital-presence",
  },
  {
    n: "02",
    title: { en: "Build", ar: "البناء" },
    body: {
      en: "Create websites and applications that help customers and your team.",
      ar: "بناء مواقع وتطبيقات تخدم عملاءك وفريقك.",
    },
    to: "/services/custom-software",
  },
  {
    n: "03",
    title: { en: "Integrate", ar: "الربط" },
    body: {
      en: "Keep customer, sales and order information together.",
      ar: "جمع معلومات العملاء والمبيعات والطلبات في مكان واحد.",
    },
    to: "/services/erp-crm",
  },
  {
    n: "04",
    title: { en: "Automate", ar: "الأتمتة" },
    body: {
      en: "Reduce repeated tasks and make follow-up easier.",
      ar: "تقليل المهام المتكررة وتسهيل المتابعة.",
    },
    to: "/services/automation",
  },
  {
    n: "05",
    title: { en: "Intelligence", ar: "الذكاء" },
    body: {
      en: "Help your team prepare replies, find information and read reports.",
      ar: "مساعدة فريقك على تجهيز الردود والوصول للمعلومات وقراءة التقارير.",
    },
    to: "/services/ai-solutions",
  },
]

export default function Home() {
  const t = useT()
  const publishedWork = caseStudies.filter((c) => c.published)

  const why: {
    icon: AnyIcon
    title: { en: string; ar: string }
    body: { en: string; ar: string }
  }[] = [
    {
      icon: "user",
      title: { en: "Business first", ar: "نبدأ من العمل" },
      body: {
        en: "We understand your goals and daily work, then agree the solution that helps your team.",
        ar: "نفهم أهدافك والعمل اليومي، ثم نتفق على الحل الذي يساعد فريقك.",
      },
    },
    {
      icon: "code",
      title: { en: "Made for your business", ar: "حلول تناسب أعمالك" },
      body: {
        en: "We shape each project around what your customers and team need.",
        ar: "نصمم كل مشروع حسب ما يحتاجه عملاؤك وفريقك.",
      },
    },
    {
      icon: "erp",
      title: { en: "Systems that connect", ar: "أنظمة مترابطة" },
      body: {
        en: "Your website and business tools share information, so your team does not enter it twice.",
        ar: "يتشارك موقعك وأدوات عملك المعلومات حتى لا يضطر فريقك إلى إدخالها مرتين.",
      },
    },
    {
      icon: "workflow",
      title: { en: "Automation where it helps", ar: "أتمتة حيث تفيد" },
      body: {
        en: "We look for repeated manual steps in every project and automate those that are stable.",
        ar: "نبحث في كل مشروع عن الخطوات اليدوية المتكررة ونؤتمت المستقر منها.",
      },
    },
    {
      icon: "cloud",
      title: { en: "Built to scale", ar: "قابلة للتوسع" },
      body: {
        en: "Start with what you need today, with room to add more as your business grows.",
        ar: "ابدأ بما تحتاجه اليوم، مع إمكانية إضافة المزيد عند نمو أعمالك.",
      },
    },
    {
      icon: "shield",
      title: { en: "Long-term support", ar: "دعم طويل الأمد" },
      body: {
        en: "Monitoring, maintenance and improvement after launch — through real use.",
        ar: "مراقبة وصيانة وتحسين بعد الإطلاق، بناءً على الاستخدام الفعلي.",
      },
    },
  ]

  const autoExamples = [
    { en: "Automated customer follow-ups", ar: "متابعة العملاء تلقائيًا" },
    { en: "Automatic sales follow-up", ar: "متابعة المبيعات تلقائيًا" },
    {
      en: "Keeping department information up to date",
      ar: "تحديث معلومات الأقسام",
    },
    { en: "Easy-to-read reports", ar: "تقارير سهلة القراءة" },
    {
      en: "Help with repeated tasks",
      ar: "مساعدة في المهام المتكررة",
    },
    {
      en: "Reminders when something needs attention",
      ar: "تنبيهات عند الحاجة للمتابعة",
    },
  ]

  return (
    <>
      {}
      <section className="relative overflow-hidden bg-white">
        <HeroBackground />
        <Container className="relative grid items-center gap-12 pb-20 pt-14 md:pb-28 md:pt-20 lg:min-h-[calc(100svh-73px)] lg:grid-cols-2 lg:gap-8 lg:py-16">
          <div className="min-w-0 rise">
            <Eyebrow>
              {t({
                en: "Digital Solutions for Your Business",
                ar: "حلول رقمية لأعمالك",
              })}
            </Eyebrow>
            <h1 className="brand-h1 mt-6 font-medium tracking-tight text-ink">
              {t({
                en: "Many paths to your goal. We choose and build the right one for your needs.",
                ar: "طرق كثيرة للوصول لهدفك، لكننا نختار ونبني لك الأنسب وفق احتياجاتك.",
              })}
            </h1>
            <p className="mt-6 max-w-xl text-lg text-slate md:text-xl md:leading-8">
              {t({
                en: "From idea to execution, we turn complex technology choices into a clear path shaped around your business goals.",
                ar: "من الفكرة إلى التنفيذ، نحوّل الخيارات التقنية المعقدة إلى مسار واضح يناسب أهداف عملك.",
              })}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button to="/start-project">
                {t({
                  en: "Start your project journey",
                  ar: "ابدأ مسار مشروعك",
                })}
              </Button>
              <Button to="/solutions" variant="secondary">
                {t({ en: "Explore our solutions", ar: "استكشف حلولنا" })}
              </Button>
            </div>
            <p className="mt-10 text-base text-slate">
              {t({
                en: "From digital presence to intelligent operations.",
                ar: "من التواجد الرقمي إلى العمليات الذكية.",
              })}
            </p>
          </div>
          <div className="min-w-0">
            <HeroFlow />
          </div>
        </Container>
      </section>

      <Section tone="mist" id="composer">
        <div className="grid items-center gap-8 rounded-r24 border border-line bg-white p-6 sm:p-8 lg:grid-cols-12 lg:gap-12 lg:p-12">
          <div className="min-w-0 lg:col-span-7">
            <Eyebrow>ORKEST Composer</Eyebrow>
            <h2 className="brand-h2 mt-4 font-medium tracking-tight text-ink">
              {t({
                en: "Your project starts with a clear brief.",
                ar: "رحلة مشروعك تبدأ بمتطلبات واضحة.",
              })}
            </h2>
            <p className="mt-5 max-w-2xl text-base leading-8 text-slate md:text-lg">
              {t({
                en: "ORKEST Composer is the starting point for your project journey with ORKEST. Choose your services, define your needs and share examples that inspire you. Our team then reviews your brief and contacts you to agree on the next steps and prepare a tailored proposal.",
                ar: "ORKEST Composer هو نقطة البداية لمسار مشروعك مع أوركست. اختر الخدمات، وحدّد احتياجاتك، وأضف نماذج تلهمك؛ ثم يراجع فريقنا موجز المشروع ويتواصل معك للاتفاق على الخطوات التالية وإعداد عرض مخصص.",
              })}
            </p>
            <Button to="/start-project" className="mt-8">
              {t({ en: "Start with ORKEST Composer", ar: "ابدأ مسار مشروعك" })}
            </Button>
          </div>
          <div className="min-w-0 lg:col-span-5">
            <ol className="grid gap-3">
              {[
                {
                  n: "01",
                  title: { en: "Choose your services", ar: "اختر خدماتك" },
                  body: { en: "One service or a complete solution.", ar: "خدمة واحدة أو حل متكامل." },
                },
                {
                  n: "02",
                  title: { en: "Shape your project brief", ar: "حدّد متطلبات مشروعك" },
                  body: { en: "Answer optional questions and share references.", ar: "أجب عن الأسئلة الاختيارية وأضف المراجع." },
                },
                {
                  n: "03",
                  title: { en: "Plan the next steps together", ar: "نحدّد الخطوات التالية معًا" },
                  body: { en: "ORKEST reviews your brief and follows up with you.", ar: "يراجع فريق أوركست طلبك ويتواصل معك." },
                },
              ].map((item) => (
                <li key={item.n} className="flex items-start gap-4 rounded-r16 border border-line bg-mist p-4 sm:p-5">
                  <span className="inline-flex size-11 shrink-0 items-center justify-center rounded-r8 bg-ice text-sm font-medium tabular-nums text-blue">
                    {item.n}
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base font-medium text-ink">{t(item.title)}</h3>
                    <p className="mt-1 text-sm leading-6 text-slate">{t(item.body)}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Section>


      <Section tone="mist">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <SectionHeading
              eyebrow={t({ en: "About us", ar: "من نحن" })}
              title={t({ en: "Technology that works with you.", ar: "تكنولوجيا تعمل معك." })}
              lead={t({
                en: "Orkest Digital Solutions builds websites, applications and connected business systems. We understand your daily work and turn your needs into practical digital solutions.",
                ar: "أوركست لتكنولوجيا المعلومات تبني المواقع والتطبيقات وأنظمة الأعمال المترابطة. نفهم عملك اليومي ونحوّل احتياجاتك إلى حلول رقمية عملية.",
              })}
            />
            <Button to="/about" variant="ghost" className="mt-6">
              {t({ en: "Get to know Orkest", ar: "تعرّف على أوركست" })}
            </Button>
          </div>
          <div className="rounded-r16 bg-white p-8 lg:col-span-5 lg:p-10">
            <span className="inline-flex size-12 items-center justify-center rounded-r8 bg-ice text-blue">
              <Icon name="workflow" />
            </span>
            <h3 className="mt-6 text-2xl font-medium text-ink">
              {t({ en: "Our vision", ar: "رؤيتنا" })}
            </h3>
            <p className="mt-4 text-lg leading-8 text-slate">
              {t({
                en: "We see a future where digital technology and automation make work simpler, connect information and give people more time to create and grow.",
                ar: "نرى مستقبلًا تجعل فيه التكنولوجيا الرقمية والأتمتة الأعمال أبسط، والمعلومات مترابطة، وتمنح الناس وقتًا أكبر للإبداع والنمو.",
              })}
            </p>
          </div>
        </div>
      </Section>

      <Section>
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading
            eyebrow={t({ en: "Core services", ar: "خدماتنا الأساسية" })}
            title={t({
              en: "Many capabilities. One ORKEST.",
              ar: "خدمات متعددة. شريك واحد.",
            })}
            lead={t({
              en: "Choose one service or combine several. We agree the work around your goals and budget.",
              ar: "اختر خدمة واحدة أو اجمع عدة خدمات. نتفق على العمل حسب أهدافك وميزانيتك.",
            })}
          />
          <Button to="/services" variant="ghost" className="shrink-0 self-start whitespace-nowrap md:self-auto">
            {t({ en: "All services", ar: "جميع الخدمات" })}
          </Button>
        </div>
        <ol className="mt-10 grid gap-4 md:mt-12 md:grid-cols-2 md:gap-6">
          {coreServices.map((s, i) => (
            <li key={s.slug} className="min-w-0">
              <Link
                to={`/services/${s.slug}`}
                className="group flex h-full flex-col rounded-r16 border border-line bg-white p-6 transition-colors hover:border-blue hover:bg-mist md:p-8"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-r8 bg-ice text-blue">
                    <Icon name={s.icon} />
                  </span>
                  <span aria-hidden="true" dir="ltr" className="inline-flex h-9 min-w-12 items-center justify-center rounded-r8 border border-line px-3 font-sans text-sm font-medium tabular-nums text-slate">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-6 text-[22px] font-medium leading-normal text-ink md:text-2xl">{t(s.title)}</h3>
                <p className="mt-3 flex-1 text-base leading-7 text-slate">{t(s.summary)}</p>
                <div className="mt-6 flex items-center justify-between gap-3 border-t border-line pt-5 text-base font-medium text-blue">
                  <span>{t({ en: "Learn more", ar: "اعرف المزيد" })}</span>
                  <Icon name="arrow" flip className="size-5 shrink-0 transition-transform motion-reduce:transition-none ltr:group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
                </div>
              </Link>
            </li>
          ))}
        </ol>
      </Section>

      {}
      <Section tone="ink">
        <SectionHeading
          light
          eyebrow={t({ en: "The transformation journey", ar: "رحلة التحول" })}
          title={t({
            en: "Start where you are. Move at your pace.",
            ar: "ابدأ من حيث أنت، وتقدّم بالوتيرة التي تناسبك.",
          })}
          lead={t({
            en: "These stages are a map, not a package. Many clients start at one stage and add others as the business grows.",
            ar: "هذه المراحل خريطة وليست باقة إلزامية. يبدأ كثير من العملاء بمرحلة واحدة ويضيفون غيرها مع نمو أعمالهم.",
          })}
        />
        <ol className="relative mt-16 grid gap-4 md:grid-cols-5">
          <span
            className="absolute inset-x-0 top-7 hidden h-0.5 bg-white/15 md:block"
            aria-hidden
          />
          {journey.map((j, i) => (
            <li key={j.n} className="relative">
              <Link
                to={j.to}
                className="group block h-full rounded-r16 p-0 md:pe-4"
              >
                <span
                  className={`relative z-10 inline-flex size-14 items-center justify-center rounded-r16 text-lg font-medium ${
                    i === 4 ? "bg-white text-blue" : "bg-blue text-white"
                  } ring-4 ring-ink transition-transform group-hover:-translate-y-0.5`}
                >
                  {j.n}
                </span>
                <h3 className="mt-6 text-xl font-medium text-white">
                  {t(j.title)}
                </h3>
                <p className="mt-2 text-base leading-7 text-white">
                  {t(j.body)}
                </p>
              </Link>
            </li>
          ))}
        </ol>
      </Section>

      {}
      <Section>
        <SectionHeading
          eyebrow={t({ en: "Industry solutions", ar: "حلول القطاعات" })}
          title={t({
            en: "Built around how your industry works.",
            ar: "مبنية حول طريقة عمل قطاعك.",
          })}
          lead={t({
            en: "Solutions for the daily challenges your customers and team face.",
            ar: "حلول للتحديات اليومية التي يواجهها عملاؤك وفريقك.",
          })}
        />
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {solutions.map((s) => (
            <LinkCard
              key={s.slug}
              to={`/solutions/${s.slug}`}
              title={t(s.title)}
              body={t(s.summary)}
            />
          ))}
        </div>
      </Section>

      {}
      {publishedWork.length > 0 && (
        <Section tone="mist">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <SectionHeading
              eyebrow={t({ en: "Selected work", ar: "أعمال مختارة" })}
              title={t({
                en: "Work we are proud of.",
                ar: "أعمال نعتز بها.",
              })}
              lead={t({
                en: "A selection of projects and the needs they address.",
                ar: "مجموعة من المشاريع والاحتياجات التي تلبيها.",
              })}
            />
            <Button to="/work" variant="ghost">
              {t({ en: "All work", ar: "جميع الأعمال" })}
            </Button>
          </div>
          <div className="mt-14 grid gap-5 lg:grid-cols-3">
            {publishedWork.map((c) => (
              <LinkCard
                key={c.slug}
                to={`/work/${c.slug}`}
                eyebrow={t(c.industry)}
                title={t(c.title)}
                body={t(c.challenge)}
              />
            ))}
          </div>
        </Section>
      )}

      {}
      <Section tone="blue" className="relative overflow-hidden">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading
              light
              eyebrow={t({
                en: "Easier Daily Work",
                ar: "عمل يومي أسهل",
              })}
              title={t({
                en: "Less repeated work. More time for your customers.",
                ar: "عمل متكرر أقل. وقت أكبر لعملائك.",
              })}
              lead={t({
                en: "Help your team prepare replies, follow requests and read reports, while you approve the steps that matter.",
                ar: "ساعد فريقك على تجهيز الردود ومتابعة الطلبات وقراءة التقارير، مع موافقتك على الخطوات المهمة.",
              })}
            />
            <div className="mt-10 flex flex-wrap gap-3">
              <Button to="/services/ai-solutions" variant="inverse">
                {t({
                  en: "Smart business assistance",
                  ar: "مساعدات ذكية للأعمال",
                })}
              </Button>
              <Link
                to="/services/automation"
                className="inline-flex min-h-12 items-center rounded-r8 px-5 font-medium text-white ring-1 ring-white/40 hover:bg-white/10"
              >
                {t({ en: "Simplifying daily work", ar: "تبسيط العمل اليومي" })}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid gap-3 sm:grid-cols-2">
              {autoExamples.map((e, i) => (
                <li
                  key={e.en}
                  className="flex items-start gap-4 rounded-r16 bg-white/[0.07] p-5 ring-1 ring-white/15"
                >
                  <span className="text-sm font-medium text-white">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="text-base font-medium text-white">
                    {t(e)}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {[
                {
                  i: "shield" as const,
                  l: { en: "Protecting your information", ar: "حماية معلوماتك" },
                },
                {
                  i: "eye" as const,
                  l: { en: "Clear progress", ar: "متابعة واضحة" },
                },
                {
                  i: "user" as const,
                  l: { en: "Human approval", ar: "موافقة بشرية" },
                },
              ].map((g) => (
                <div
                  key={g.l.en}
                  className="flex items-center gap-3 rounded-r16 bg-white p-4 text-blue"
                >
                  <Icon name={g.i} className="size-5" />
                  <span className="text-base font-medium">{t(g.l)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {}
      <Section>
        <SectionHeading
          eyebrow={t({ en: "Why ORKEST", ar: "لماذا أوركست" })}
          title={t({
            en: "Clear words. Quiet confidence.",
            ar: "كلام واضح. وثقة هادئة.",
          })}
          lead={t({
            en: "We explain the work and the change it brings — and we make every part of your digital business work together.",
            ar: "نشرح العمل والتغيير الذي يحدثه، ونجعل كل جزء من أعمالك الرقمية يعمل مع الآخر.",
          })}
        />
        <div className="mt-14 grid gap-px overflow-hidden rounded-r24 border border-line bg-line md:grid-cols-2 lg:grid-cols-3">
          {why.map((w) => (
            <div key={w.title.en} className="bg-white p-8">
              <Icon name={w.icon} className="size-7 text-blue" />
              <h3 className="mt-6 text-xl font-medium text-ink">
                {t(w.title)}
              </h3>
              <p className="mt-3 text-base leading-7 text-slate">{t(w.body)}</p>
            </div>
          ))}
        </div>
      </Section>

      {}
      <CEOMessage />

      <CTASection
        title={t({
          en: "Where do you want your business to be next year?",
          ar: "أين تريد أن تكون أعمالك في العام القادم؟",
        })}
      />
    </>
  )
}
